import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
  TemplateRef,
  untracked,
  viewChild,
} from '@angular/core';
import { debouncedSignal } from '@softline/core';
import {
  ButtonComponent,
  IconComponent,
  InputComponent,
  ItemComponent,
  ItemDescriptionComponent,
  ItemLeadingComponent,
  ItemTitleComponent,
  ItemTrailingComponent,
  LoadingSkeletonComponent,
  modalFn,
  ResolvableModal,
  SearchInputComponent,
  SoftSlotDirective,
  SOFTLINE_MODAL_RESOLVER,
  TranslatePipe,
} from '@softline/ui-core';
import { DsTabComponent, DsTabsComponent } from '@softline/ui-core/tabs';
import {
  ConstructionSiteFormComponent,
  SiteMapThumbComponent,
} from '@softapps/core/customer-info';
import {
  MASTERDATA_CONSTRUCTION_SITE,
  MASTERDATA_DEFAULT_PAGE_SIZE,
  MasterdataCustomer,
  MasterdataEntityDefinition,
  MasterdataService,
} from '@softapps/core/masterdata';
import { Subscription } from 'rxjs';
import {
  DocumentFormData,
  EMPTY_DOCUMENT_FORM_DATA,
} from '../../../../types/document-form-data';
import { constructionSiteNumberLabel } from '../../document-creation-wizard.functions';

@Component({
  selector: 'soft-sales-construction-site-step',
  templateUrl: './construction-site-step.component.html',
  styleUrl: './construction-site-step.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-col h-full min-w-100' },
  imports: [
    TranslatePipe,
    DsTabComponent,
    DsTabsComponent,
    ItemComponent,
    ItemLeadingComponent,
    ItemTitleComponent,
    ItemDescriptionComponent,
    ItemTrailingComponent,
    IconComponent,
    SearchInputComponent,
    ButtonComponent,
    SoftSlotDirective,
    LoadingSkeletonComponent,
    InputComponent,
    SiteMapThumbComponent,
  ],
})
export class ConstructionSiteStepComponent
  extends ResolvableModal<DocumentFormData>
  implements OnInit
{
  private readonly modalResolver = inject(SOFTLINE_MODAL_RESOLVER, { optional: true });
  private readonly masterdata = inject(MasterdataService);
  private readonly modal = modalFn();

  readonly data = input.required<DocumentFormData>();
  readonly isEmbedded = input(false);
  readonly local = signal<DocumentFormData>(EMPTY_DOCUMENT_FORM_DATA);
  readonly next = output<DocumentFormData>();
  readonly dataChange = output<DocumentFormData>();
  readonly create = output<DocumentFormData>();

  override readonly bottomTemplate = viewChild('bottomTpl', { read: TemplateRef });

  readonly search = signal('');
  private readonly debouncedSearch = debouncedSignal(this.search, 200);
  readonly skeletonRows = Array.from({ length: 6 });

  private readonly customerNumber = computed(
    () => this.local().customer?.customerNumber ?? null
  );

  /** Wizard variant: keeps the main account (subnumber 0) in the list — it renders
   *  as the explicit "Keine Baustelle" row, exactly like the field-ok did. Built at
   *  construction: a module-scope spread runs while the definition may still be
   *  uninitialized in the bundle's import cycle. */
  private readonly wizardConstructionSite: MasterdataEntityDefinition<MasterdataCustomer> =
    {
      ...MASTERDATA_CONSTRUCTION_SITE,
      defaultFilter: [
        { type: 'booleanIsTrue', name: 'active' },
        { type: 'booleanIsFalse', name: 'blocked' },
      ],
    };

  /* Server-side search + paging (masterdata) — the term searches ALL of the
     customer's sites, scrolling appends the next page. */
  private readonly sites = signal<MasterdataCustomer[]>([]);
  readonly constructionSites = this.sites.asReadonly();
  readonly constructionSitesTotal = signal<number | null>(null);
  readonly loading = signal(false);
  readonly loadingMore = signal(false);
  readonly hasMore = computed(() => {
    const total = this.constructionSitesTotal();
    return total != null && this.constructionSites().length < total;
  });

  private sitesSubscription: Subscription | null = null;
  private sitesOffset = 0;

  private readonly siteQuery = computed<{
    customerNumber: number;
    term: string;
  } | null>(() => {
    const number = this.customerNumber();
    if (number == null) return null;
    return { customerNumber: Number(number), term: this.debouncedSearch().trim() };
  });

  private readonly loadSitesOnQueryChange = effect(() => {
    const query = this.siteQuery();
    untracked(() => this.loadConstructionSites(query));
  });

  private readonly disposeSitesSubscription = inject(DestroyRef).onDestroy(() =>
    this.sitesSubscription?.unsubscribe()
  );

  private loadConstructionSites(
    query: { customerNumber: number; term: string } | null
  ): void {
    this.sitesSubscription?.unsubscribe();
    this.sitesSubscription = null;
    this.loadingMore.set(false);
    this.sitesOffset = 0;
    if (!query) {
      this.sites.set([]);
      this.constructionSitesTotal.set(0);
      this.loading.set(false);
      return;
    }
    this.loading.set(true);
    this.sitesSubscription = this.masterdata
      .searchWithCount$(this.wizardConstructionSite, query.term, {
        filter: {
          type: 'numberEquals',
          name: 'customerNumber',
          value: query.customerNumber,
        },
        limit: MASTERDATA_DEFAULT_PAGE_SIZE,
        offset: 0,
      })
      .subscribe({
        next: ({ items, totalCount }) => {
          this.sites.set(items);
          this.constructionSitesTotal.set(totalCount);
          this.sitesOffset = items.length;
          this.loading.set(false);
        },
        error: () => {
          this.sites.set([]);
          this.constructionSitesTotal.set(null);
          this.loading.set(false);
        },
      });
  }

  loadMoreConstructionSites(): void {
    if (this.loading() || this.loadingMore() || !this.hasMore()) return;
    const query = this.siteQuery();
    if (!query) return;
    const offset = this.sitesOffset;
    this.loadingMore.set(true);
    this.sitesSubscription?.unsubscribe();
    this.sitesSubscription = this.masterdata
      .searchWithCount$(this.wizardConstructionSite, query.term, {
        filter: {
          type: 'numberEquals',
          name: 'customerNumber',
          value: query.customerNumber,
        },
        limit: MASTERDATA_DEFAULT_PAGE_SIZE,
        offset,
      })
      .subscribe({
        next: ({ items, totalCount }) => {
          this.sites.update((current) => [...current, ...items]);
          this.constructionSitesTotal.set(totalCount);
          this.sitesOffset = offset + items.length;
          this.loadingMore.set(false);
        },
        error: () => this.loadingMore.set(false),
      });
  }

  /** Appends the next page when the list scrolls near its end. */
  onListScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= 200) {
      this.loadMoreConstructionSites();
    }
  }

  protected readonly selectedKey = computed(() => {
    const id = this.local().constructionSiteId;
    return id != null ? String(id) : null;
  });

  protected readonly mode = signal<'search' | 'manual'>('search');
  protected readonly manualMode = computed(() => this.mode() === 'manual');
  protected readonly manualName = signal('');

  protected readonly isManualSelected = computed(
    () => !!this.local().constructionSite?.trim(),
  );

  protected readonly canApply = computed(() =>
    this.manualMode()
      ? this.manualName().trim().length > 0
      : this.selectedKey() !== null
  );

  protected readonly footerHint = computed(() => {
    if (this.manualMode()) {
      const label = this.manualName().trim();
      return label
        ? `Freie Bezeichnung: ${label}`
        : 'Optional — freie Bezeichnung eingeben';
    }
    const id = this.local().constructionSiteId;
    const row =
      id != null
        ? this.constructionSites().find((site) => Number(site.id) === id)
        : null;
    if (row) {
      return [this.rowTitle(row), constructionSiteNumberLabel(row)]
        .filter(Boolean)
        .join(' · ');
    }
    if (id != null) return this.local().constructionSiteName ?? 'Baustelle gewählt';
    return 'Optional — ohne Baustelle fortfahren möglich';
  });

  /** A site seeded or created by id gets its label once the list knows it. */
  private readonly backfillSiteName = effect(() => {
    const rows = this.constructionSites();
    const current = untracked(() => this.local());
    if (current.constructionSiteId == null || current.constructionSiteName) return;
    const row = rows.find((site) => Number(site.id) === current.constructionSiteId);
    if (!row) return;
    untracked(() => {
      this.local.update((d) => ({
        ...d,
        constructionSiteName: [constructionSiteNumberLabel(row), this.rowTitle(row)]
          .filter(Boolean)
          .join(' · '),
      }));
      this.dataChange.emit(this.local());
    });
  });

  ngOnInit(): void {
    const seed = untracked(() => this.data());
    this.local.set(seed);
    const existing = seed.constructionSite;
    if (existing != null) {
      this.manualName.set(existing);
      if (existing.trim().length > 0) this.mode.set('manual');
    }
  }

  selectConstructionSite(site: MasterdataCustomer): void {
    const pickedId = site?.id != null ? Number(site.id) || null : null;
    this.local.update((d) => ({
      ...d,
      constructionSiteId: pickedId,
      constructionSite: null,
      constructionSiteName:
        pickedId != null
          ? [constructionSiteNumberLabel(site), this.rowTitle(site)]
              .filter(Boolean)
              .join(' · ')
          : null,
    }));
    this.mode.set('search');
    this.dataChange.emit(this.local());
  }

  setMode(next: 'search' | 'manual'): void {
    this.mode.set(next);
  }

  /** Opens the Baustelle-erstellen form (handoff dialog 6); on save the new
      site is selected by its id and the list reloads to show its master data. */
  protected async openCreateForm(): Promise<void> {
    const customerId = this.local().customer?.id;
    if (customerId == null) return;
    const result = await this.modal({
      component: ConstructionSiteFormComponent,
      title: 'Baustelle erstellen',
      subtitle: 'Neue Baustelle für diesen Kunden anlegen',
      icon: 'fa-regular fa-helmet-safety',
      dismiss: true,
      data: { customerId: String(customerId) },
    });
    if (!result || result === 'DISMISSED') return;
    const createdId = Number(result) || null;
    if (createdId === null) return;
    this.local.update((d) => ({
      ...d,
      constructionSiteId: createdId,
      constructionSite: null,
      constructionSiteName: null,
    }));
    this.mode.set('search');
    this.dataChange.emit(this.local());
    this.loadConstructionSites(this.siteQuery());
  }

  protected onApply(): void {
    if (!this.canApply()) return;
    if (this.manualMode()) {
      const value = this.manualName().trim();
      this.local.update((d) => ({
        ...d,
        constructionSiteId: null,
        constructionSite: value,
        constructionSiteName: null,
      }));
      this.dataChange.emit(this.local());
    }
    this.confirm();
  }

  protected onWithoutSite(): void {
    this.local.update((d) => ({
      ...d,
      constructionSiteId: null,
      constructionSite: null,
      constructionSiteName: null,
    }));
    this.dataChange.emit(this.local());
    this.confirm();
  }

  rowKey(site: MasterdataCustomer): string {
    return site.id != null ? String(site.id) : '';
  }

  rowNumberLabel(site: MasterdataCustomer): string {
    return site.customerSubnumber > 0
      ? `· ${site.customerNumber}/${site.customerSubnumber}`
      : `· ${site.customerNumber}`;
  }

  isNoConstructionSite(site: MasterdataCustomer): boolean {
    return !site.customerSubnumber || site.customerSubnumber === 0;
  }

  rowAddress(site: MasterdataCustomer): string | null {
    const street = [site.street, site.streetNumber]
      .map((s) => s?.trim())
      .filter(Boolean)
      .join(' ');
    const cityLine = [site.postCode, site.city]
      .map((s) => s?.trim())
      .filter(Boolean)
      .join(' ');
    const result = [street, cityLine].filter(Boolean).join(', ');
    return result.length > 0 ? result : null;
  }

  rowCustomerName(site: MasterdataCustomer): string {
    return site.shortName?.trim() || '—';
  }

  rowConstructionSiteName(site: MasterdataCustomer): string | null {
    return site.name1?.trim() || null;
  }

  rowTitle(site: MasterdataCustomer): string {
    if (this.isNoConstructionSite(site)) return 'Keine Baustelle';
    return this.rowConstructionSiteName(site) ?? this.rowCustomerName(site);
  }

  set(key: keyof DocumentFormData, value: unknown): void {
    this.local.update((d) => ({ ...d, [key]: value }));
    this.dataChange.emit(this.local());
  }

  confirm(): void {
    const result = this.local();
    if (this.modalResolver && !this.isEmbedded()) {
      this.resolve(result);
    } else {
      this.next.emit(result);
    }
  }
}
